import axios from 'axios';
import { Product } from '../redux/slices/productSlice';

// Interface para representar um chunk de texto extraído de um documento
export interface TextChunk {
  content: string;
  metadata: {
    source: string;
    page?: number;
    position?: number;
    productId?: string;
    productType?: string;
  };
  embedding?: number[];
  score?: number;
}

// Interface para armazenar documentos processados
export interface ProcessedDocument {
  id: string;
  filename: string;
  type: string;
  chunks: TextChunk[];
  createdAt: Date;
}

// Interface para representar um arquivo de conhecimento
export interface KnowledgeFile {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadDate: Date;
  status: 'processing' | 'ready' | 'error';
  progress: number;
  chunks?: string[];
  embeddings?: number[][];
  error?: string;
}

// Interface para opções de RAG
export interface RAGOptions {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  personality?: string;
}

// Classe para gerenciar o armazenamento de embeddings e chunks
export class VectorStore {
  private documents: Map<string, ProcessedDocument>;
  
  constructor() {
    this.documents = new Map();
  }

  // Adicionar um documento processado ao armazenamento
  addDocument(document: ProcessedDocument): void {
    this.documents.set(document.id, document);
  }

  // Remover um documento do armazenamento
  removeDocument(id: string): boolean {
    return this.documents.delete(id);
  }

  // Obter um documento específico
  getDocument(id: string): ProcessedDocument | undefined {
    return this.documents.get(id);
  }

  // Obter todos os documentos
  getAllDocuments(): ProcessedDocument[] {
    return Array.from(this.documents.values());
  }
  
  /**
   * Processa produtos e os adiciona à base de conhecimento
   * @param products Lista de produtos para adicionar à base de conhecimento
   * @returns Lista de arquivos de conhecimento criados
   */
  processProducts(products: Product[]): Promise<KnowledgeFile[]> {
    return new Promise(async (resolve) => {
      const knowledgeFiles: KnowledgeFile[] = [];
      
      // Processar cada produto
      for (const product of products) {
        // Criar um ID único para o arquivo de conhecimento
        const fileId = `product_${product.id}_${Date.now()}`;
        
        // Criar conteúdo detalhado do produto
        const productContent = this.createProductContent(product);
        
        // Dividir o conteúdo em chunks
        const chunks = this.splitIntoChunks(productContent);
        
        // Gerar embeddings para cada chunk
        const embeddings = await this.generateEmbeddings(chunks);
        
        // Criar arquivo de conhecimento
        const knowledgeFile: KnowledgeFile = {
          id: fileId,
          name: `Produto: ${product.name}`,
          type: 'product',
          size: productContent.length,
          uploadDate: new Date(),
          status: 'ready',
          progress: 100,
          chunks,
          embeddings
        };
        
        // Adicionar aos documentos processados
        this.documents.set(fileId, {
          id: fileId,
          filename: knowledgeFile.name,
          type: 'product',
          chunks: this.createTextChunks(chunks).map(chunk => ({
            ...chunk,
            metadata: {
              ...chunk.metadata,
              productId: product.id,
              productType: product.type
            }
          })),
          createdAt: new Date()
        });
        
        knowledgeFiles.push(knowledgeFile);
      }
      
      resolve(knowledgeFiles);
    });
  }
  
  /**
   * Cria conteúdo detalhado para um produto
   * @param product Produto para criar conteúdo
   * @returns Texto detalhado sobre o produto
   */
  private createProductContent(product: Product): string {
    let content = `PRODUTO: ${product.name}\n\n`;
    content += `CÓDIGO: ${product.code}\n`;
    content += `TIPO: ${product.type === 'produto' ? 'Produto' : 'Serviço'}\n`;
    content += `CATEGORIA: ${product.category || 'Não categorizado'}\n`;
    content += `PREÇO: R$ ${product.price.toFixed(2)}\n\n`;
    content += `DESCRIÇÃO:\n${product.description}\n\n`;
    
    if (product.type === 'produto') {
      content += `Este é um produto físico disponível para compra imediata. `;
    } else {
      content += `Este é um serviço que pode ser contratado. `;
    }
    
    content += `Para mais informações sobre ${product.name}, entre em contato com nossos consultores.\n\n`;
    
    // Adicionar informações específicas por categoria
    if (product.category?.toLowerCase().includes('imóvel') || 
        product.category?.toLowerCase().includes('casa') || 
        product.category?.toLowerCase().includes('apartamento')) {
      content += `INFORMAÇÕES ADICIONAIS PARA IMÓVEIS:\n`;
      content += `- Este imóvel está disponível para visitas mediante agendamento.\n`;
      content += `- Aceitamos financiamento bancário e FGTS para aquisição.\n`;
      content += `- Documentação completa e regularizada.\n`;
    }
    
    return content;
  }

  /**
   * Atualiza um arquivo existente com um novo conteúdo
   */
  async updateFile(fileId: string, newFile: File): Promise<KnowledgeFile> {
    // Encontrar o arquivo existente no mapa de documentos
    if (!this.documents.has(fileId)) {
      throw new Error('Arquivo não encontrado para atualização');
    }
    
    // Remover o documento existente
    this.documents.delete(fileId);
    
    // Processar o novo arquivo com o mesmo ID
    return new Promise((resolve, reject) => {
      const updatedFile: KnowledgeFile = {
        id: fileId, // Mantém o mesmo ID
        name: newFile.name,
        type: newFile.type,
        size: newFile.size,
        uploadDate: new Date(),
        status: 'processing',
        progress: 0,
      };
      
      // Simulação de processamento do arquivo
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          // Simulação de extração de texto do arquivo
          const text = e.target?.result as string || '';
          
          // Dividir o texto em chunks
          const chunks = this.splitIntoChunks(text);
          
          // Simulação de progresso
          updatedFile.progress = 50;
          
          // Gerar embeddings para cada chunk
          const embeddings = await this.generateEmbeddings(chunks);
          
          // Atualizar o status do arquivo
          updatedFile.status = 'ready';
          updatedFile.progress = 100;
          updatedFile.chunks = chunks;
          updatedFile.embeddings = embeddings;
          
          // Adicionar aos documentos processados
          this.documents.set(updatedFile.id, {
            id: updatedFile.id,
            filename: updatedFile.name,
            type: updatedFile.type,
            chunks: this.createTextChunks(chunks),
            createdAt: new Date()
          });
          
          resolve(updatedFile);
        } catch (error) {
          updatedFile.status = 'error';
          reject(error);
        }
      };
      
      reader.onerror = () => {
        updatedFile.status = 'error';
        reject(new Error('Erro ao ler o arquivo'));
      };
      
      // Iniciar a leitura do arquivo
      reader.readAsText(newFile);
      
      // Simulação de progresso inicial
      updatedFile.progress = 10;
    });
  }

  /**
   * Processa um arquivo e extrai seu conteúdo para uso no RAG
   */
  async processFile(file: File): Promise<KnowledgeFile> {
    return new Promise((resolve, reject) => {
      const newFile: KnowledgeFile = {
        id: Date.now().toString(),
        name: file.name,
        type: file.type,
        size: file.size,
        uploadDate: new Date(),
        status: 'processing',
        progress: 0,
      };
      
      // Simulação de processamento do arquivo
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          // Simulação de extração de texto do arquivo
          const text = e.target?.result as string || '';
          
          // Dividir o texto em chunks
          const chunks = this.splitIntoChunks(text);
          
          // Simulação de progresso
          newFile.progress = 50;
          
          // Gerar embeddings para cada chunk
          const embeddings = await this.generateEmbeddings(chunks);
          
          // Atualizar o status do arquivo
          newFile.status = 'ready';
          newFile.progress = 100;
          newFile.chunks = chunks;
          newFile.embeddings = embeddings;
          
          // Adicionar aos documentos processados
          this.documents.set(newFile.id, {
            id: newFile.id,
            filename: newFile.name,
            type: newFile.type,
            chunks: this.createTextChunks(chunks),
            createdAt: new Date()
          });
          
          resolve(newFile);
        } catch (error) {
          newFile.status = 'error';
          reject(error);
        }
      };
      
      reader.onerror = () => {
        newFile.status = 'error';
        reject(new Error('Erro ao ler o arquivo'));
      };
      
      // Iniciar a leitura do arquivo
      reader.readAsText(file);
      
      // Simulação de progresso inicial
      newFile.progress = 10;
    });
  }

  /**
   * Gera uma resposta usando RAG
   */
  async generateRAGResponse(query: string, options: RAGOptions = {}): Promise<string> {
    // Recuperar chunks relevantes
    const relevantChunks = await this.retrieveRelevantChunks(query);
    
    if (relevantChunks.length === 0) {
      return "Desculpe, não tenho informações suficientes para responder a essa pergunta. Por favor, carregue documentos relevantes para que eu possa ajudar melhor.";
    }
    
    // Construir o contexto
    const context = relevantChunks.map(chunk => chunk.content).join('\n\n');
    
    // Construir o prompt
    const prompt = `
    Você é um assistente de WhatsApp especializado em fornecer respostas baseadas em documentos.
    Contexto extraído dos documentos:
    ${context}
    Pergunta do usuário: ${query}
    Responda à pergunta do usuário com base APENAS nas informações fornecidas no contexto acima.`;
    
    // Simulação de chamada à API de completions
    // Em um ambiente real, isso seria uma chamada à API OpenAI ou similar
    return this.simulateCompletionAPI(prompt, options);
  }

  /**
   * Recupera os chunks mais relevantes para uma consulta
   */
  private async retrieveRelevantChunks(query: string, topK: number = 3): Promise<TextChunk[]> {
    if (this.documents.size === 0) {
      return [];
    }
    
    // Simulação de embedding da consulta
    const queryEmbedding = Array.from({ length: 10 }, () => Math.random());
    
    // Simulação de busca por similaridade
    const allChunks: { chunk: TextChunk; score: number }[] = [];
    
    this.documents.forEach(doc => {
      doc.chunks.forEach(chunk => {
        // Simulação de cálculo de similaridade (produto escalar)
        const embedding = chunk.embedding || Array.from({ length: 10 }, () => Math.random());
        let score = 0;
        for (let j = 0; j < queryEmbedding.length; j++) {
          score += queryEmbedding[j] * embedding[j];
        }
        
        allChunks.push({ chunk, score });
      });
    });
    
    // Ordenar por score e pegar os top K
    allChunks.sort((a, b) => b.score - a.score);
    return allChunks.slice(0, topK).map(item => item.chunk);
  }

  /**
   * Divide um texto em chunks menores
   */
  private splitIntoChunks(text: string): string[] {
    const chunkSize = 1000; // Tamanho aproximado de cada chunk
    const overlap = 200; // Sobreposição entre chunks
    
    const chunks: string[] = [];
    
    // Dividir por parágrafos primeiro
    const paragraphs = text.split(/\n\s*\n/);
    
    let currentChunk = '';
    let position = 0;
    
    for (const paragraph of paragraphs) {
      // Se adicionar este parágrafo exceder o tamanho do chunk
      if (currentChunk.length + paragraph.length > chunkSize && currentChunk.length > 0) {
        // Salvar o chunk atual
        chunks.push(currentChunk.trim());
        
        // Iniciar novo chunk com sobreposição
        const words = currentChunk.split(' ');
        currentChunk = words.slice(Math.max(0, words.length - overlap / 10)).join(' ');
        position += 1;
      }
      
      currentChunk += '\n\n' + paragraph;
    }
    
    // Adicionar o último chunk se não estiver vazio
    if (currentChunk.trim().length > 0) {
      chunks.push(currentChunk.trim());
    }
    
    return chunks;
  }

  /**
   * Gera embeddings para um conjunto de chunks de texto
   */
  private async generateEmbeddings(chunks: string[]): Promise<number[][]> {
    // Simulação de chamada a uma API de embeddings
    return chunks.map(() => {
      // Gerar um vetor de embedding aleatório de dimensão 10
      return Array.from({ length: 10 }, () => Math.random());
    });
  }

  /**
   * Cria objetos TextChunk a partir de strings
   */
  private createTextChunks(chunks: string[]): TextChunk[] {
    return chunks.map((chunk, index) => ({
      content: chunk,
      metadata: {
        source: 'documento',
        position: index,
      },
      embedding: Array.from({ length: 10 }, () => Math.random()),
    }));
  }

  /**
   * Simulação de uma API de completions
   */
  private async simulateCompletionAPI(prompt: string, options: RAGOptions): Promise<string> {
    // Simulação de delay para parecer uma chamada de API real
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Respostas simuladas para diferentes tipos de perguntas
    const responses = [
      "Com base nos documentos fornecidos, o imóvel na Rua das Flores possui 3 quartos, 2 banheiros, uma área de 120m² e está localizado próximo ao centro da cidade.",
      "De acordo com as informações disponíveis, o valor do aluguel é de R$ 2.500,00 por mês, com condomínio de R$ 500,00 e IPTU de R$ 100,00 mensais.",
      "Os documentos indicam que o processo de financiamento pode ser feito diretamente com o banco de sua preferência, com entrada mínima de 20% do valor do imóvel.",
      "Conforme as informações nos arquivos, a entrega do empreendimento está prevista para dezembro de 2023, com possibilidade de visita ao decorado a partir do próximo mês.",
      "Segundo os documentos, este imóvel possui acabamento de alto padrão, com piso em porcelanato, teto com gesso e iluminação em LED.",
    ];
    
    // Selecionar uma resposta aleatória
    const randomIndex = Math.floor(Math.random() * responses.length);
    
    // Ajustar a resposta com base na personalidade
    let response = responses[randomIndex];
    
    if (options.personality === 'friendly') {
      response = `Oi! 😊 ${response} Posso ajudar com mais alguma coisa?`;
    } else if (options.personality === 'professional') {
      response = `${response} Estou à disposição para esclarecer qualquer dúvida adicional.`;
    } else if (options.personality === 'concise') {
      response = response.split('.')[0] + '.';
    }
    
    return response;
  }
}

// Singleton para o armazenamento de vetores
export const vectorStore = new VectorStore();

// Exportar funções para uso externo
export const processFile = (file: File) => vectorStore.processFile(file);
export const generateRAGResponse = (query: string, options: RAGOptions) => vectorStore.generateRAGResponse(query, options);
export const removeDocument = (id: string) => vectorStore.removeDocument(id);
export const updateFile = (fileId: string, newFile: File) => vectorStore.updateFile(fileId, newFile);
export const processProducts = (products: Product[]) => vectorStore.processProducts(products);
